import { CheckboxColor } from "@/common/enum/checkbox-color";
import { DeleteBulkAdminNoticeModal } from "@/components/modal/DeleteBulkAdminNoticeModal";
import { Footnote12Description } from "@/components/typography/Footnote12Description";
import { BasePageDto } from "@/types/dto/base-page-dto";
import { NoticeDetailDto } from "@/types/dto/notice-detail-dto";
import { TableHeader } from "@/types/table-header";
import { Checkbox, Table } from "flowbite-react";
import { useState } from "react";
import { CustomCell } from "../CustomCell";
import { AdminNoticeFooter } from "./AdminNoticeFooter";
import { AdminNoticeRow } from "./AdminNoticeRow";

interface Props {
  page: BasePageDto<NoticeDetailDto>;
  handleNoticeNameClick: (id: number) => void;
  handlePageChange: (currentPage: number) => void;
  onSuccess: () => void;
}

const headers: TableHeader[] = [
  { title: "노출 상태", width: "w-[97px]" },
  { title: "노출 기간", width: "w-[219px]" },
  { title: "공지사항 제목", width: "w-[1075px]" },
  { title: "작성자", width: "w-[87px]" },
  { title: "생성 날짜", width: "w-[118px]" },
];

export const AdminNoticeTable: React.FC<Props> = ({
  page,
  handleNoticeNameClick,
  handlePageChange,
  onSuccess,
}) => {
  const [checkedNoticeIds, setCheckedNoticeIds] = useState<number[]>([]);
  const [isOpenDeleteBulkNoticeModal, setIsOpenDeleteBulkNoticeModal] =
    useState(false);
  const notices = page.contents;

  const handleDeleteBulkNoticeModalOpen = () => {
    setIsOpenDeleteBulkNoticeModal(true);
  };

  const handleDeleteBulkNoticeModalClose = () => {
    setIsOpenDeleteBulkNoticeModal(false);
  };

  const handleCheckAll = () => {
    const isChecked = checkedNoticeIds.length === notices.length;
    if (isChecked) {
      setCheckedNoticeIds([]);
    } else {
      const allItemIds = notices.map((notice) => notice.id);
      setCheckedNoticeIds(allItemIds);
    }
  };

  const clearCheckedNoticeIds = () => {
    setCheckedNoticeIds([]);
  };

  const handleCheckItem = (noticeId: number) => {
    const isChecked = checkedNoticeIds.includes(noticeId);
    if (isChecked) {
      setCheckedNoticeIds((prev) => prev.filter((id) => id !== noticeId));
    } else {
      setCheckedNoticeIds((prev) => [...prev, noticeId]);
    }
  };

  return (
    <div className="overflow-x-auto rounded-lg border">
      <Table>
        <Table.Head>
          <Table.HeadCell className="w-[52px] flex items-center justify-center">
            <Checkbox
              color={
                checkedNoticeIds.length === notices.length
                  ? CheckboxColor.DARK
                  : ""
              }
              checked={checkedNoticeIds.length === notices.length}
              onChange={handleCheckAll}
            />
          </Table.HeadCell>
          {headers.map((header) => (
            <Table.HeadCell
              key={header.title}
              className={header.width + " h-[52px] p-0 border-b"}
            >
              <CustomCell>
                <Footnote12Description
                  className={"text-semantic-text-secondary"}
                >
                  {header.title}
                </Footnote12Description>
              </CustomCell>
            </Table.HeadCell>
          ))}
        </Table.Head>
        <Table.Body className="divide-y">
          {notices.map((data) => (
            <AdminNoticeRow
              key={data.id}
              data={data}
              handleNoticeNameClick={handleNoticeNameClick}
              checkedNoticeIds={checkedNoticeIds}
              handleCheckItem={handleCheckItem}
            />
          ))}
          <AdminNoticeFooter
            headersLength={Object.values(headers).length}
            page={page}
            checkedNoticeIds={checkedNoticeIds}
            handlePageChange={handlePageChange}
            handleDeleteBulkNoticeModalOpen={handleDeleteBulkNoticeModalOpen}
          />
        </Table.Body>
      </Table>
      {checkedNoticeIds.length > 0 && isOpenDeleteBulkNoticeModal && (
        <DeleteBulkAdminNoticeModal
          noticeIds={checkedNoticeIds}
          closeModal={handleDeleteBulkNoticeModalClose}
          clearSelectedNotice={clearCheckedNoticeIds}
          onSuccess={onSuccess}
        />
      )}
    </div>
  );
};
